import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { Fragment, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/ui";

interface BatchMultiSelectOption {
  id: number | string;
  name: string;
}

interface BatchMultiSelectProps<T extends number | string> {
  value: T[];
  items: BatchMultiSelectOption[];
  onChange: (values: T[]) => void;

  withNone?: boolean;
  noneValue?: T;

  label?: string;
  placeholder?: string;
  className?: string;
  error?: string;

  disabled?: boolean;
  isReadOnly?: boolean;
}

export default function BatchMultiSelect<T extends number | string>({
  value,
  items,
  onChange,
  withNone = false,
  noneValue,
  label,
  placeholder,
  className,
  error,
  disabled = false,
  isReadOnly = false,
}: BatchMultiSelectProps<T>) {
  const { t: translation } = useTranslation();
  const hasError = Boolean(error);
  const isEmpty = items.length === 0;
  const isDisabled = disabled || isReadOnly || isEmpty;

  const defaultPlaceholder = translation("common.dropdown.select");
  const noneText = translation("common.dropdown.none");
  const noElementsText = translation("common.dropdown.no-elements");
  const cannotBeUpdatedText = translation("common.dropdown.cannot-be-updated");

  const disabledMessage = useMemo(() => {
    if (isEmpty) return noElementsText;
    if (isReadOnly) return cannotBeUpdatedText;
    return "";
  }, [isEmpty, isReadOnly, noElementsText, cannotBeUpdatedText]);

  const options = useMemo<BatchMultiSelectOption[]>(() => {
    if (!withNone || noneValue === undefined) return items;
    return [{ id: noneValue, name: noneText }, ...items];
  }, [items, withNone, noneValue, noneText]);

  const handleChange = (selected: T[]) => {
    if (!withNone || noneValue === undefined) {
      onChange(selected);
      return;
    }

    onChange(selected.includes(noneValue) ? [noneValue] : selected.filter((v) => v !== noneValue));
  };

  const hasNone = withNone && noneValue !== undefined;

  const shouldReturnNoneValue = useCallback(
    (): boolean => hasNone && value.length === 1 && value[0] === noneValue,
    [hasNone, value, noneValue]
  );

  const getItemNumber = useCallback(
    (): number => (hasNone ? value.filter((v) => v !== noneValue).length : value.length),
    [hasNone, value, noneValue]
  );

  const shouldReturnSelectedName = useCallback((): string => {
    const selectedId = hasNone ? value.find((v) => v !== noneValue) : value[0];
    return items.find((item) => item.id === selectedId)?.name ?? "";
  }, [hasNone, value, noneValue, items]);

  const displayValue = useMemo(() => {
    if (value.length === 0) return placeholder || defaultPlaceholder;

    if (shouldReturnNoneValue()) {
      return noneText;
    }

    const itemNumber = getItemNumber();

    if (itemNumber === 1) {
      const itemName = shouldReturnSelectedName();
      if (itemName) return itemName;
    }

    return translation("common.dropdown.selected", { count: itemNumber });
  }, [
    value,
    placeholder,
    defaultPlaceholder,
    noneText,
    translation,
    shouldReturnNoneValue,
    getItemNumber,
    shouldReturnSelectedName,
  ]);

  return (
    <div className={className}>
      <Listbox multiple value={value} onChange={handleChange} disabled={isDisabled}>
        {({ open }) => (
          <>
            {label && <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</Label>}

            <div className={cn("relative", label && "mt-1")}>
              <ListboxButton
                title={isDisabled ? disabledMessage : undefined}
                className={cn(
                  "relative w-full rounded-md py-2 pl-3 pr-10 text-left shadow-sm sm:text-sm",
                  "border focus:outline-none focus:ring-1",
                  "dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200",
                  isDisabled ? "cursor-not-allowed bg-gray-100 dark:bg-gray-800" : "cursor-pointer",
                  hasError
                    ? "border-red-300 focus:border-red-500 focus:ring-red-400 dark:border-red-400"
                    : "border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500"
                )}
              >
                <span className={cn("block truncate", isDisabled && "text-gray-400")}>
                  {isDisabled ? disabledMessage : displayValue}
                </span>

                {!isDisabled && (
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="size-5 text-gray-400" />
                  </span>
                )}
              </ListboxButton>

              {options.length > 0 && (
                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-700">
                    {options.map(({ id, name }) => {
                      const isSelected = value.includes(id as T);
                      const isNone = withNone && noneValue !== undefined && id === noneValue;

                      return (
                        <ListboxOption
                          key={id}
                          value={id}
                          className="relative cursor-pointer select-none py-2 pl-10 pr-4 text-gray-900 data-[focus]:bg-blue-100 data-[focus]:text-blue-900 dark:text-gray-100 dark:data-[focus]:bg-blue-900 dark:data-[focus]:text-blue-100"
                        >
                          <span
                            className={cn(
                              "block truncate",
                              isSelected ? "font-medium" : "font-normal",
                              isNone && "italic"
                            )}
                          >
                            {name}
                          </span>

                          {isSelected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400">
                              <CheckIcon className="size-5" />
                            </span>
                          )}
                        </ListboxOption>
                      );
                    })}
                  </ListboxOptions>
                </Transition>
              )}
            </div>
          </>
        )}
      </Listbox>

      {hasError && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
